import { useEffect, useState } from 'react';

import classnames from 'classnames';

import { getFileExtension } from '@/helpers/file';
import { loadImageFromUrl } from '@/helpers/image';
import { BlobToDataURL } from '@/helpers/parsers';

import FileIcon, { FileIconProps } from '../FileIcon';
import { Loader } from '../Loader';

import styles from './index.module.scss';

type FileImagePreviewProps = {
  alt?: string;
  className?: string;
  file: string | File;
  extension?: string;
  size?: number;
  fit?: boolean;
};

export default function FileImagePreview({ className, file, extension, fit, size = 100, alt = 'Preview image' }: FileImagePreviewProps) {
  const [image, setImage] = useState<string>();
  const [imgElement, setImgElement] = useState<HTMLImageElement>();
  const [loading, setLoading] = useState(true);

  const getFileIcon = (extension: string) => {
    const extensionColorMap: Record<string, FileIconProps['color']> = {
      csv: 'green',
      jpg: 'orange',
      jpeg: 'orange',
      png: 'orange',
      gif: 'orange',
      pdf: 'red',
      mp3: 'teal',
      mp4: 'purple',
      mpg: 'purple',
      mpeg: 'purple',
    };

    return <FileIcon color={extensionColorMap[extension as keyof typeof extensionColorMap]} extension={extension} />;
  };

  useEffect(() => {
    const parseImage = async () => {
      try {
        const url = file instanceof File ? await BlobToDataURL(file) : file;
        const loadedImgElement = await loadImageFromUrl(url);

        setImgElement(loadedImgElement);
        setImage(url);
      } catch (err) {
        setImage(undefined);
      } finally {
        setLoading(false);
      }
    };

    parseImage();
  }, [file]);

  const fileExtension = extension || getFileExtension(file);

  return (
    <div className={classnames(styles.wrapper, className)}>
      <div className={styles.inner} style={{ width: size, height: size }}>
        {loading ? (
          <Loader size={30} strokeSize={4} isRelative />
        ) : image && imgElement?.complete ? (
          <div className={classnames(styles.imageContainer, fit && styles.fit)}>
            <img alt={alt} src={image} />
          </div>
        ) : (
          getFileIcon(fileExtension)
        )}
      </div>
    </div>
  );
}
